import { useState, useRef, useEffect } from 'react';
import Chart from 'react-apexcharts';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdSearch, MdHome, MdApartment, MdStorefront } from 'react-icons/md';

interface CityOption {
    _id: string;
    name: string;
    postalCode: string;
    codeGeo: string;
}

interface SalesStats {
    count: number;
    avgPricePerM2: number | null;
    medPricePerM2: number | null;
}

interface CityDetail {
    _id: string;
    name: string;
    postalCode: string;
    codeGeo: string;
    apartments: SalesStats;
    houses: SalesStats;
    stores: SalesStats;
}

interface DeptRow {
    dept: string;
    apartments: number;
    houses: number;
    total: number;
    pctApartments: number;
    pctHouses: number;
}

function StatCard({ icon, label, stats, color }: {
    icon: React.ReactNode;
    label: string;
    stats: SalesStats;
    color: string;
}) {
    return (
        <Card extra="p-5">
            <div className={`mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide ${color}`}>
                {icon}
                {label}
            </div>
            <div className="grid grid-cols-3 gap-4">
                <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Ventes</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {stats.count.toLocaleString('fr-FR')}
                    </p>
                </div>
                <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Prix moy. /m²</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {stats.avgPricePerM2 != null ? `${stats.avgPricePerM2.toLocaleString('fr-FR')} €` : '—'}
                    </p>
                </div>
                <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Prix méd. /m²</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        {stats.medPricePerM2 != null ? `${stats.medPricePerM2.toLocaleString('fr-FR')} €` : '—'}
                    </p>
                </div>
            </div>
        </Card>
    );
}

export default function BackOfficeCityPrices() {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState<CityOption[]>([]);
    const [selected, setSelected] = useState<CityDetail | null>(null);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [open, setOpen] = useState(false);
    const [deptRows, setDeptRows] = useState<DeptRow[]>([]);
    const [loadingDept, setLoadingDept] = useState(true);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        fetchDeptBreakdown();
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const fetchDeptBreakdown = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${CONFIG.apiUrl}/admin/city-prices-dept-breakdown`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setDeptRows(res.data.rows);
        } catch {
            setDeptRows([]);
        } finally {
            setLoadingDept(false);
        }
    };

    const handleQueryChange = (value: string) => {
        setQuery(value);
        setOpen(true);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (value.trim().length < 2) {
            setSuggestions([]);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            try {
                setLoadingSearch(true);
                const token = localStorage.getItem('token');
                const res = await axios.get(`${CONFIG.apiUrl}/admin/city-prices/search`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { q: value.trim() },
                });
                setSuggestions(res.data.cities);
            } catch {
                setSuggestions([]);
            } finally {
                setLoadingSearch(false);
            }
        }, 250);
    };

    const selectCity = async (city: CityOption) => {
        setQuery(`${city.name} (${city.postalCode})`);
        setOpen(false);
        setSuggestions([]);
        try {
            setLoadingDetail(true);
            const token = localStorage.getItem('token');
            const res = await axios.get(`${CONFIG.apiUrl}/admin/city-prices/${city.codeGeo}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setSelected(res.data.city);
        } catch {
            setSelected(null);
        } finally {
            setLoadingDetail(false);
        }
    };

    const chartOptions: ApexCharts.ApexOptions = {
        chart: { type: 'bar', stacked: true, stackType: '100%', toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: '70%' } },
        xaxis: {
            categories: deptRows.map((r) => `Dept. ${r.dept}`),
            labels: { formatter: (v) => `${v}%` },
        },
        yaxis: { labels: { style: { fontSize: '11px' } } },
        tooltip: {
            y: {
                formatter: (_val, { seriesIndex, dataPointIndex }) => {
                    const row = deptRows[dataPointIndex];
                    if (!row) return '';
                    return seriesIndex === 0
                        ? `${row.pctApartments}% (${row.apartments.toLocaleString('fr-FR')} ventes)`
                        : `${row.pctHouses}% (${row.houses.toLocaleString('fr-FR')} ventes)`;
                },
            },
        },
        colors: ['#4f46e5', '#16a34a'],
        legend: { position: 'top' },
        dataLabels: {
            enabled: true,
            formatter: (val: number) => (val > 5 ? `${val.toFixed(0)}%` : ''),
            style: { fontSize: '11px' },
        },
    };

    const chartSeries = [
        { name: 'Appartements', data: deptRows.map((r) => r.pctApartments) },
        { name: 'Maisons', data: deptRows.map((r) => r.pctHouses) },
    ];

    return (
        <div className="mt-5 space-y-5">
            {/* Dept breakdown chart */}
            <Card extra="p-5">
                <h2 className="mb-1 text-lg font-bold text-gray-900 dark:text-white">
                    Appartements vs Maisons par département
                </h2>
                <p className="mb-4 text-xs text-gray-400">Répartition en % du volume de ventes, trié par total décroissant</p>
                {loadingDept ? (
                    <p className="py-10 text-center text-sm text-gray-400">Chargement…</p>
                ) : deptRows.length === 0 ? (
                    <p className="py-10 text-center text-sm text-gray-400">Aucune donnée — lancez la migration d'import.</p>
                ) : (
                    <div style={{ height: Math.max(400, deptRows.length * 22) }}>
                        <Chart
                            options={chartOptions}
                            series={chartSeries}
                            type="bar"
                            height="100%"
                        />
                    </div>
                )}
            </Card>

            {/* City search */}
            <Card extra="p-5">
                <h2 className="mb-4 text-lg font-bold text-gray-900 dark:text-white">Prix immobiliers par ville</h2>

                <div ref={wrapperRef} className="relative max-w-md">
                    <div className="flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 dark:border-navy-600 dark:bg-navy-800">
                        <MdSearch className="h-5 w-5 shrink-0 text-gray-400" />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => handleQueryChange(e.target.value)}
                            onFocus={() => suggestions.length > 0 && setOpen(true)}
                            placeholder="Rechercher une ville…"
                            className="w-full bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400 dark:text-white"
                        />
                        {loadingSearch && <span className="text-xs text-gray-400">…</span>}
                    </div>

                    {open && suggestions.length > 0 && (
                        <ul className="absolute z-10 mt-1 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-navy-600 dark:bg-navy-800">
                            {suggestions.map((city) => (
                                <li key={city._id}>
                                    <button
                                        type="button"
                                        onClick={() => selectCity(city)}
                                        className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-navy-700"
                                    >
                                        <span className="font-medium text-gray-900 dark:text-white">{city.name}</span>
                                        <span className="text-xs text-gray-400">{city.postalCode}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </Card>

            {loadingDetail && <p className="text-center text-sm text-gray-400">Chargement…</p>}

            {selected && !loadingDetail && (
                <>
                    <div className="flex items-baseline gap-3">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">{selected.name}</h3>
                        <span className="text-sm text-gray-500">Code INSEE : {selected.codeGeo}</span>
                    </div>

                    <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                        <StatCard
                            icon={<MdApartment className="h-4 w-4" />}
                            label="Appartements"
                            stats={selected.apartments}
                            color="text-blue-600 dark:text-blue-400"
                        />
                        <StatCard
                            icon={<MdHome className="h-4 w-4" />}
                            label="Maisons"
                            stats={selected.houses}
                            color="text-green-600 dark:text-green-400"
                        />
                        <StatCard
                            icon={<MdStorefront className="h-4 w-4" />}
                            label="Locaux commerciaux"
                            stats={selected.stores}
                            color="text-purple-600 dark:text-purple-400"
                        />
                    </div>

                    {(() => {
                        const total = selected.apartments.count + selected.houses.count;
                        if (total === 0) return null;
                        const pctApt = Math.round((selected.apartments.count / total) * 100);
                        const pctHouse = 100 - pctApt;
                        return (
                            <Card extra="p-5">
                                <p className="mb-3 text-sm font-bold text-gray-700 dark:text-gray-200">
                                    Répartition appartements / maisons
                                </p>
                                <div className="flex h-5 w-full overflow-hidden rounded-full">
                                    <div
                                        className="flex items-center justify-center bg-indigo-500 text-xs font-semibold text-white transition-all"
                                        style={{ width: `${pctApt}%` }}
                                    >
                                        {pctApt > 8 ? `${pctApt}%` : ''}
                                    </div>
                                    <div
                                        className="flex items-center justify-center bg-green-500 text-xs font-semibold text-white transition-all"
                                        style={{ width: `${pctHouse}%` }}
                                    >
                                        {pctHouse > 8 ? `${pctHouse}%` : ''}
                                    </div>
                                </div>
                                <div className="mt-2 flex gap-4 text-xs text-gray-500 dark:text-gray-400">
                                    <span className="flex items-center gap-1">
                                        <span className="inline-block h-2.5 w-2.5 rounded-full bg-indigo-500" />
                                        Appartements — {selected.apartments.count.toLocaleString('fr-FR')} ventes ({pctApt}%)
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <span className="inline-block h-2.5 w-2.5 rounded-full bg-green-500" />
                                        Maisons — {selected.houses.count.toLocaleString('fr-FR')} ventes ({pctHouse}%)
                                    </span>
                                </div>
                            </Card>
                        );
                    })()}
                </>
            )}
        </div>
    );
}
